import React, { useState } from 'react';

/*
 * ValoracionPopup — Campaña "Opiniones Google" (diseño aprobado por Ann 2026-07-14).
 *
 * Flujo:
 *  1. "¿Te gusta la nueva plataforma?" + 5 estrellas + comentario libre (opcional).
 *  2. 1-4 estrellas → se guarda el voto → "Gracias".
 *  3. 5 estrellas → se guardan SIEMPRE + recuadro DORADO METÁLICO invitando a Google.
 *  4. Pantalla Google: caja con su opinión + "Copiar mi opinión" → "Abrir Google y pegar".
 *     (Honesto: Google NO permite auto-publicar; la persona pega y publica desde su cuenta.)
 *
 * Marca: teal #077069 / #2C6675, Montserrat, dorado metálico SOLO en el bloque de Google.
 * Guardado real: POST /canal-valoraciones (a conectar). Aquí onGuardar recibe el voto.
 */

// Enlace DIRECTO a la caja de reseña de Google del centro (Ann 2026-07-25): abre la
// ventana de «Escribir reseña» sin que la clienta tenga que buscar el botón en la
// ficha (el enlace maps/place caía en la ficha → «no me aparecía el botón»). Es el
// mismo enlace canónico que ya usa el resto de la app (ClientTopBar, CanalPilarDashboard).
const GOOGLE_REVIEW_URL = 'https://g.page/r/CRWuzRPOXq60EAE/review';

const T = {
  teal: '#077069', teal2: '#2C6675', mint: '#8FD3C8',
  ink: '#14312d', star: '#F5A623', starOff: '#d8e3e0',
  gold1: '#C9A24B', gold2: '#F4E4A6', gold3: '#B8860B',
};

function Estrella({ on, onClick, onHover, onLeave }) {
  return (
    <button type="button" onClick={onClick} onMouseEnter={onHover} onMouseLeave={onLeave}
      aria-label="estrella"
      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, lineHeight: 1 }}>
      <svg width="44" height="44" viewBox="0 0 24 24" style={{ transition: 'transform .12s' }}>
        <path d="M12 2.5l2.9 5.9 6.5.95-4.7 4.58 1.11 6.47L12 17.4l-5.81 3.05 1.11-6.47L2.6 9.35l6.5-.95L12 2.5z"
          fill={on ? T.star : 'none'} stroke={on ? T.star : T.starOff} strokeWidth="1.4" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

export default function ValoracionPopup({ nombre = '', onClose = () => {}, onGuardar = () => {}, reabierto = false, estrellasPrevias = 0 }) {
  // Reenvío (Ann 2026-07-25): si el admin reabrió la reseña, NO se piden las estrellas
  // otra vez (ya las sabemos) → se arranca directo en el paso «reintento», que solo
  // manda a Google con el aviso de que la última no se registró.
  const [paso, setPaso] = useState(() => (reabierto ? 'reintento' : 'rate')); // rate | gracias | wow | reintento | graciasfinal
  const [estrellas, setEstrellas] = useState(() => (reabierto ? (estrellasPrevias || 5) : 0));
  const [hover, setHover] = useState(0);
  const nombreCorto = (nombre || '').split(' ')[0] || '';

  const guardar = (fueGoogle) => {
    try { onGuardar({ estrellas, comentario: '', fue_a_google: !!fueGoogle }); } catch (e) { /* noop */ }
  };
  const enviar = () => {
    if (estrellas < 1) return;
    guardar(false); // el voto (estrellas) se guarda SIEMPRE al enviar — es NUESTRA estadística
    setPaso(estrellas >= 5 ? 'wow' : 'gracias');
  };
  // 5 estrellas: abre Google directamente (la reseña la escribe la persona allí) y da las gracias.
  const compartir = () => {
    guardar(true); // marca fue_a_google (dato para nosotros)
    try { window.open(GOOGLE_REVIEW_URL, '_blank', 'noopener'); } catch (e) { /* noop */ }
    setPaso('graciasfinal');
  };

  const nEstrellas = hover || estrellas;

  return (
    <div style={S.ov} onClick={(e) => { if (e.currentTarget === e.target) onClose(); }}>
      <style>{CSS}</style>
      <div className="vpop" role="dialog" aria-modal="true">
        <button className="vpop-x" onClick={onClose} aria-label="Cerrar">✕</button>

        {paso === 'rate' && (
          <>
            <h2 className="vpop-t">¿Te gusta la nueva plataforma?</h2>
            <p className="vpop-sub">Tu opinión es muy valiosa.</p>
            <div className="vpop-stars">
              {[1, 2, 3, 4, 5].map(n => (
                <Estrella key={n} on={n <= nEstrellas}
                  onClick={() => setEstrellas(n)}
                  onHover={() => setHover(n)} onLeave={() => setHover(0)} />
              ))}
            </div>
            <button className="vpop-btn" disabled={estrellas < 1} onClick={enviar} style={{ marginTop: 4 }}>Enviar</button>
          </>
        )}

        {paso === 'gracias' && (
          <div className="vpop-center">
            <div className="vpop-emoji">💚</div>
            <h2 className="vpop-t">¡Gracias{nombreCorto ? `, ${nombreCorto}` : ''}!</h2>
            <p className="vpop-sub">Tu opinión nos ayuda a mejorar y la valoramos muy especialmente.</p>
            <button className="vpop-btn" onClick={onClose}>Cerrar</button>
          </div>
        )}

        {paso === 'graciasfinal' && (
          <div className="vpop-center">
            <div className="vpop-emoji">💚&nbsp;🙏</div>
            <h2 className="vpop-t">¡Gracias{nombreCorto ? `, ${nombreCorto}` : ''}!</h2>
            <p className="vpop-sub">Por opiniones así es por lo que nuestro esfuerzo cobra sentido.</p>
            <button className="vpop-btn" onClick={onClose}>Cerrar</button>
          </div>
        )}

        {paso === 'wow' && (
          <div className="vpop-center">
            <div className="vpop-stars vpop-stars--sm">
              {[1, 2, 3, 4, 5].map(n => <Estrella key={n} on onClick={() => {}} onHover={() => {}} onLeave={() => {}} />)}
            </div>
            <div className="vpop-gold">
              <h2 className="vpop-wow">🌟 ¡Wow{nombreCorto ? `, ${nombreCorto}` : ''}, muchas gracias!</h2>
              <p className="vpop-goldtxt">
                ¿Lo compartes en Google? Es 1 minuto para ti y un empujón enorme para nosotros. 💛
              </p>
              <button className="vpop-btn vpop-btn--gold" onClick={compartir}>Compartir</button>
              <p style={{ fontSize: 12.5, color: '#7a5b00', margin: '10px 0 0', lineHeight: 1.4 }}>
                👉 Se abrirá Google. Si te lo pide, <b>entra con tu cuenta</b>; luego <b>vuelve a marcar las estrellas</b> y escribe tu opinión ahí.
              </p>
            </div>
            <button className="vpop-later" onClick={onClose}>Ahora no</button>
            <p style={{ fontSize: 11.5, color: '#8aa39d', margin: '8px 0 0', lineHeight: 1.4 }}>
              ¿Prefieres otro momento? Puedes hacerlo cuando quieras desde <b>«Opinar»</b> en tu menú.
            </p>
          </div>
        )}

        {/* REINTENTO (Ann 2026-07-25): reenvío desde el admin. No se piden estrellas
            otra vez; se va directa a Google avisando de que la última no se registró. */}
        {paso === 'reintento' && (
          <div className="vpop-center">
            <div className="vpop-stars vpop-stars--sm">
              {[1, 2, 3, 4, 5].map(n => <Estrella key={n} on={n <= (estrellas || 5)} onClick={() => {}} onHover={() => {}} onLeave={() => {}} />)}
            </div>
            <div className="vpop-gold">
              <h2 className="vpop-wow">🌟 ¡Gracias de nuevo{nombreCorto ? `, ${nombreCorto}` : ''}!</h2>
              <p className="vpop-goldtxt">
                <b>Google no registró tu opinión</b> la última vez. ¿La intentas de nuevo, por favor? Es 1 minuto. 💛
              </p>
              <button className="vpop-btn vpop-btn--gold" onClick={compartir}>Reintentar en Google</button>
              <p style={{ fontSize: 12.5, color: '#7a5b00', margin: '10px 0 0', lineHeight: 1.4 }}>
                👉 Se abrirá Google. Si te lo pide, <b>entra con tu cuenta</b>; luego marca las estrellas y escribe tu opinión ahí.
              </p>
            </div>
            <button className="vpop-later" onClick={onClose}>Ahora no</button>
          </div>
        )}

      </div>
    </div>
  );
}

const S = {
  ov: {
    position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'rgba(8,24,22,.55)', backdropFilter: 'blur(3px)', padding: 16,
  },
};

const CSS = `
.vpop{font-family:'Montserrat',-apple-system,BlinkMacSystemFont,sans-serif; position:relative;
  width:100%; max-width:420px; background:#fff; border-radius:22px; padding:30px 26px 26px;
  box-shadow:0 24px 70px rgba(0,0,0,.35); text-align:center; animation:vpop-in .28s ease}
@keyframes vpop-in{from{opacity:0; transform:translateY(14px) scale(.97)}to{opacity:1; transform:none}}
.vpop-x{position:absolute; top:12px; right:14px; background:none; border:none; font-size:20px;
  color:#9bb3ad; cursor:pointer; line-height:1}
.vpop-t{color:${T.teal}; font-size:24px; font-weight:800; margin:4px 0 6px; line-height:1.2}
.vpop-sub{color:#4a5f5b; font-size:15.5px; margin:0 0 18px; line-height:1.45}
.vpop-stars{display:flex; justify-content:center; gap:2px; margin:8px 0 16px}
.vpop-stars--sm svg{width:30px; height:30px}
.vpop-ta{width:100%; border:1.5px solid #d8e3e0; border-radius:14px; padding:12px 14px; font-size:15px;
  font-family:inherit; resize:none; box-sizing:border-box; color:${T.ink}; outline:none; margin-bottom:16px}
.vpop-ta:focus{border-color:${T.teal}}
.vpop-btn{width:100%; border:none; border-radius:14px; padding:15px; font-size:16px; font-weight:700;
  font-family:inherit; cursor:pointer; background:${T.teal}; color:#fff; transition:filter .15s, transform .1s}
.vpop-btn:hover:not(:disabled){filter:brightness(1.08)}
.vpop-btn:active:not(:disabled){transform:scale(.98)}
.vpop-btn:disabled{opacity:.4; cursor:default}
.vpop-center{display:flex; flex-direction:column; align-items:center}
.vpop-emoji{font-size:44px; margin-bottom:6px}
.vpop-gold{background:linear-gradient(145deg,${T.gold2},#fff 55%,${T.gold2}); border:2px solid ${T.gold1};
  border-radius:18px; padding:20px 18px; margin:6px 0 14px; box-shadow:0 6px 22px rgba(184,134,11,.22)}
/* Wow LEGIBLE (Ann 2026-07-25: «el wow en blanco no se ve»): color sólido forzado y
   sin relleno degradado (que lo dejaba casi blanco sobre el dorado). Verde marca. */
.vpop-wow{color:${T.teal} !important; -webkit-text-fill-color:${T.teal}; background:none;
  font-size:20px; font-weight:800; margin:0 0 10px; line-height:1.25;
  text-shadow:0 1px 0 rgba(255,255,255,.5)}
.vpop-goldtxt{color:#1a1a1a; font-size:15px; line-height:1.5; margin:0 0 16px}
.vpop-btn--gold{background:linear-gradient(145deg,${T.gold1},${T.gold3}); color:#fff;
  box-shadow:0 6px 18px rgba(184,134,11,.35)}
.vpop-copiado{background:linear-gradient(145deg,#fff,${T.gold2}) !important; color:${T.gold3} !important;
  border:1.5px solid ${T.gold1}}
.vpop-later{background:none; border:none; color:#8aa19c; font-size:14px; font-weight:600;
  cursor:pointer; margin-top:12px; font-family:inherit}
.vpop-quote{background:#f2f8f6; border:1.5px dashed ${T.mint}; border-radius:14px; padding:14px 16px;
  color:${T.ink}; font-size:15.5px; line-height:1.5; text-align:left; margin:6px 0 16px; font-style:italic}
.vpop-steps{text-align:left; color:#33463f; font-size:14.5px; line-height:1.7; margin:16px 0 4px; padding-left:20px}
.vpop-mini{color:#8aa19c; font-size:13px; margin:6px 0 0}
`;
